import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";
import { testIds } from "@/testIds";

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-[color:var(--lilia-on-primary)]",
        secondary:
          "border-transparent bg-muted text-muted-foreground",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground",
        outline: "text-foreground",
        success:
          "border-transparent bg-success-subtle text-success",
        warning:
          "border-transparent bg-warning-subtle text-warning",
        // A new feature on an existing surface (Olivia, REPLY-2026-10-06b §2):
        // the warm fill with its own ink, 11 / 700, 20 high, radius 10. At most
        // one on screen; it expires after 30 days or first use. Never on new
        // content, never on the Lilia menu or Home.
        new:
          "h-5 rounded-[10px] border-transparent px-2 py-0 text-[11px] font-bold leading-none bg-[var(--lilia-accent-warm)] text-[color:var(--lilia-accent-warm-ink)]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, children, ...props }: BadgeProps) {
  // The New badge says New unless told otherwise, and carries its testid.
  const isNew = variant === "new";
  return (
    <div
      data-testid={isNew ? testIds.warm.badgeNew : undefined}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    >
      {children ?? (isNew ? "New" : null)}
    </div>
  );
}

export { Badge, badgeVariants };
export type { BadgeProps };
